import { useTranslator } from '@bluebeam/react-i18n';
import MapLayers from 'components/Icons/MapLayers';
import QuestionSpeechBubble from 'components/Icons/QuestionSpeechBubble';
import TapeRoll from 'components/Icons/TapeRoll';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { useContext } from 'react';
import { ViewMapLayers } from 'records/interactions/ViewMapLayers';
import { ViewMapPunchList } from 'records/interactions/ViewMapPunchList';
import { ViewRFIList } from 'records/interactions/ViewRFIList';
import OverlayTabToggle from './OverlayTabToggle';

const MapPageSubheader: React.FC = () => {
  const { state: appState } = useContext(AppStoreContext);
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const { translate } = useTranslator(['mapView']);

  const isLayersTabActive = appState.interactions.some(
    (interaction) => interaction.type === 'ViewMapLayers'
  );

  const isRfiTabActive = appState.interactions.some((i) =>
    ['ViewRFI', 'ViewRFIList'].includes(i.type)
  );

  const isPunchTabActive = appState.interactions.some((interaction) =>
    ['UpdatePunch', 'ViewMapPunchList', 'CreatePunch'].includes(
      interaction.type
    )
  );

  const containerStyles = {
    top: 96,
    height: `calc(100vh - 96px)`,
  };

  const toggledSheets = appState
    .getVisibleSheetEntries()
    .valueSeq()
    .toList()
    .map((entry) => entry.sheet);

  const onTogglePunchTab = () => {
    if (isPunchTabActive) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
    } else {
      const punchListInteraction = new ViewMapPunchList({
        sheets: toggledSheets,
        containerStyles,
        resetPunchQuery: true,
      });
      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_MAP_PUNCH_LIST_OVERLAY,
        data: { interaction: punchListInteraction },
      });
    }
  };

  function onToggleRFITab() {
    if (isRfiTabActive) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
    } else {
      const viewListRFI = new ViewRFIList({
        rfis: appState.rfis.valueSeq().toList(), // I'm not sure this is actually needed now that rfis come from queried rfis.
        containerStyles,
      });
      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_RFI_LIST_OVERLAY,
        data: { interaction: viewListRFI },
      });
    }
  }

  return (
    <div className="hrem-3 bgd-white row items-center justify-between pl4">
      <div className="row items-center h100">
        <OverlayTabToggle
          id="map-navigation-layers-btn"
          active={isLayersTabActive}
          icon={
            <MapLayers
              style={{ color: 'currentcolor' }}
              width="24px"
              height="24px"
            />
          }
          text={translate('mapView:mapPageSubheader.layers')}
          onToggleClick={() => {
            if (!isLayersTabActive) {
              const interaction = new ViewMapLayers();
              mapOverlayMachineService.send({
                type: MapOverlayMachineEventsEnum.OPEN_MAP_LAYERS_OVERLAY,
                data: { interaction },
              });
            } else {
              mapOverlayMachineService.send(
                MapOverlayMachineEventsEnum.CLOSE_OVERLAY
              );
            }
          }}
        />
      </div>
      <div className="row items-center h100">
        <OverlayTabToggle
          id="map-navigation-rfis-btn"
          active={isRfiTabActive}
          icon={
            <QuestionSpeechBubble
              style={{ color: 'currentcolor' }}
              height="20px"
              width="20px"
            />
          }
          text={translate('mapView:mapPageSubheader.rfis')}
          onToggleClick={onToggleRFITab}
        />
        <OverlayTabToggle
          id="map-navigation-punch-btn"
          active={isPunchTabActive}
          icon={<TapeRoll width="18px" height="20px" />}
          text={translate('mapView:mapPageSubheader.punch')}
          onToggleClick={onTogglePunchTab}
        />
      </div>
    </div>
  );
};

export default MapPageSubheader;
